/**
 * Ambiente da Região 3 — TEMPEST REACH (Domínio da Tempestade). Bioma próprio e MUITO
 * distinto das regiões 1/2: planaltos elevados de pedra clara, pontes de pedra, ruínas
 * antigas verticais, cristais condutores altos com arcos de energia, céu tempestuoso
 * (nuvens estilizadas + relâmpagos distantes SÓ visuais), névoa de altitude, vento
 * (grama que balança + debris flutuante) e fragmentos suspensos. Verticalidade e sensação
 * de "ilhas separadas" para não parecer Shattered Wilds recolorida (§69).
 *
 * Mobile-first: tudo compartilha geometria/material via InstancedMesh, contagens modestas,
 * anima só quando o Group está visível. RegionManager mostra/esconde o Group.
 */
import * as THREE from "three";
import { type VisualTheme } from "../config/GameConfig";
import { regionHalfSize } from "./Territories";
import { randRange } from "../utils/math";

const DEBRIS = 40; // 10.9HY
export class TempestReach {
  readonly group = new THREE.Group();
  private readonly half = regionHalfSize("tempestReach"); // 10.6H
  /** 10.6J — os marcos eram contados a olho para um mapa de 210 m. A área cresceu e eles
   *  ficaram esparsos; agora a quantidade acompanha a ÁREA (proporção ao quadrado). */
  private readonly dens = Math.max(1, (this.half / 105) ** 2);
  private readonly crystalMats: THREE.MeshStandardMaterial[] = [];
  private readonly arcMats: THREE.MeshBasicMaterial[] = [];
  private cloudGroup = new THREE.Group();
  private debris!: THREE.InstancedMesh;
  private grass!: THREE.InstancedMesh;
  private readonly grassBase: THREE.Matrix4[] = [];
  private lightningMat!: THREE.MeshBasicMaterial;
  private pulse = 0;
  private lightningTimer = 2;

  constructor(
    scene: THREE.Scene,
    private readonly theme: VisualTheme,
  ) {
    this.buildGround();
    this.buildPlateaus();
    this.buildBridges();
    this.buildCrystals();
    this.buildRuins();
    this.buildFoliage();
    this.buildFloatingFragments();
    this.buildSky();
    this.buildBoundary();
    this.group.visible = false;
    scene.add(this.group);
  }

  /** 10.5S — o CHÃO agora é do Arena (bioma por território + colisão). Aqui ficou só
   *  o que é marco desta região; sem chão/grid próprios para não brigar com ele. */
  private buildGround(): void {
    /* intencionalmente vazio */
  }

  /** Planaltos elevados: plataformas de pedra clara em alturas variadas (verticalidade + "ilhas"). */
  private buildPlateaus(): void {
    const mat = new THREE.MeshStandardMaterial({ color: this.theme.stone, roughness: 0.95, flatShading: true });
    const rimMat = new THREE.MeshStandardMaterial({ color: 0xb9cbe0, roughness: 0.9, flatShading: true });
    // posições fixas (leitura consistente) — evita o sanctuary central
    const spots: { x: number; z: number; r: number; h: number }[] = [
      { x: -20, z: -18, r: 7, h: 1.4 },
      { x: 18, z: -12, r: 6, h: 1.0 },
      { x: -14, z: -40, r: 8, h: 2.2 },
      { x: 22, z: -44, r: 7, h: 1.8 },
      { x: 0, z: -54, r: 9, h: 2.8 },
      { x: -30, z: 6, r: 5, h: 0.8 },
      { x: 28, z: 14, r: 5, h: 0.9 },
    ];
    for (const s of spots) {
      const plat = new THREE.Mesh(new THREE.CylinderGeometry(s.r, s.r * 1.06, s.h, 7), mat);
      plat.position.set(s.x, s.h / 2, s.z);
      plat.rotation.y = randRange(0, Math.PI);
      plat.castShadow = true;
      plat.receiveShadow = true;
      this.group.add(plat);
      // borda clara no topo (leitura de altura)
      const rim = new THREE.Mesh(new THREE.TorusGeometry(s.r * 0.98, 0.12, 6, 7), rimMat);
      rim.rotation.x = -Math.PI / 2;
      rim.position.set(s.x, s.h + 0.02, s.z);
      this.group.add(rim);
    }
  }

  /** Pontes de pedra ligando alguns planaltos (leitura de travessia). */
  private buildBridges(): void {
    const mat = new THREE.MeshStandardMaterial({ color: 0x7f96b0, roughness: 1, flatShading: true });
    const spans: { x: number; z: number; len: number; ang: number; y: number }[] = [
      { x: -10, z: -29, len: 16, ang: -0.9, y: 1.6 },
      { x: 20, z: -28, len: 18, ang: 1.4, y: 1.3 },
      { x: 4, z: -49, len: 14, ang: 0.4, y: 2.4 },
    ];
    for (const b of spans) {
      const bridge = new THREE.Group();
      const deck = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.3, b.len), mat);
      deck.castShadow = true;
      deck.receiveShadow = true;
      bridge.add(deck);
      // pilares
      for (let i = -1; i <= 1; i += 2) {
        const pil = new THREE.Mesh(new THREE.BoxGeometry(0.5, b.y, 0.5), mat);
        pil.position.set(0, -b.y / 2, (b.len / 2 - 1) * i);
        bridge.add(pil);
      }
      bridge.position.set(b.x, b.y, b.z);
      bridge.rotation.y = b.ang;
      this.group.add(bridge);
    }
  }

  /** Cristais condutores ALTOS e finos (verticalidade) + arcos de energia entre tips. */
  private buildCrystals(): void {
    const geo = new THREE.ConeGeometry(0.5, 3, 5); // altos e finos, ≠ octaedros de R2
    const count = 70;
    const colors = [this.theme.accent, 0xbfefff, 0xd9e6ff];
    const tips: THREE.Vector3[] = [];
    for (let ci = 0; ci < colors.length; ci++) {
      const mat = new THREE.MeshStandardMaterial({ color: colors[ci], emissive: colors[ci], emissiveIntensity: 0.8, roughness: 0.25, metalness: 0.25, flatShading: true });
      this.crystalMats.push(mat);
      const inst = new THREE.InstancedMesh(geo, mat, count);
      const m = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      const e = new THREE.Euler();
      let n = 0;
      while (n < count) {
        const cx = randRange(-this.half + 5, this.half - 5);
        const cz = randRange(-this.half + 5, this.half - 5);
        if (Math.hypot(cx, cz) < 11) continue; // longe do sanctuary
        const clusterSize = 1 + Math.floor(Math.random() * 3);
        for (let k = 0; k < clusterSize && n < count; k++) {
          const s = randRange(0.6, 2.2);
          const px = cx + randRange(-1.6, 1.6);
          const pz = cz + randRange(-1.6, 1.6);
          const tilt = randRange(-0.16, 0.16);
          e.set(tilt, randRange(0, Math.PI * 2), tilt);
          q.setFromEuler(e);
          m.compose(new THREE.Vector3(px, s * 1.5, pz), q, new THREE.Vector3(s, s * 1.4, s));
          inst.setMatrixAt(n, m);
          if (ci === 0 && tips.length < 24) tips.push(new THREE.Vector3(px, s * 3, pz));
          n++;
        }
      }
      inst.instanceMatrix.needsUpdate = true;
      inst.castShadow = true;
      this.group.add(inst);
    }
    // arcos de energia: cilindros finos ligando alguns tips próximos (flicker no update)
    for (let i = 0; i < tips.length; i++) {
      for (let j = i + 1; j < tips.length; j++) {
        const a = tips[i], b = tips[j];
        const d = a.distanceTo(b);
        if (d > 6 || d < 2) continue;
        const mid = a.clone().lerp(b, 0.5);
        const arcMat = new THREE.MeshBasicMaterial({ color: this.theme.accent, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false });
        this.arcMats.push(arcMat);
        const arc = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, d, 4), arcMat);
        arc.position.copy(mid);
        arc.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
        this.group.add(arc);
        if (this.arcMats.length >= 14) return;
      }
    }
  }

  /** Ruínas antigas verticais: arcos altos e colunas quebradas em pedra clara. */
  private buildRuins(): void {
    const mat = new THREE.MeshStandardMaterial({ color: 0x9db2cc, roughness: 1, flatShading: true });
    const colGeo = new THREE.CylinderGeometry(0.4, 0.55, 5, 6);
    for (let i = 0; i < Math.round(12 * this.dens); i++) {
      let x = 0, z = 0;
      for (let t = 0; t < 6; t++) { x = randRange(-this.half + 6, this.half - 6); z = randRange(-this.half + 6, this.half - 6); if (Math.hypot(x, z) > 13) break; }
      const h = randRange(1.6, 5);
      const p = new THREE.Mesh(colGeo, mat);
      p.scale.y = h / 5;
      p.position.set(x, h / 2, z);
      p.rotation.set(randRange(-0.12, 0.12), randRange(0, 6), randRange(-0.12, 0.12));
      p.castShadow = true;
      this.group.add(p);
    }
    // grande arco cerimonial (ponto de interesse, mais alto que o de R2)
    const arch = new THREE.Group();
    const legGeo = new THREE.BoxGeometry(0.9, 6, 0.9);
    const l1 = new THREE.Mesh(legGeo, mat); l1.position.set(-3, 3, 0); l1.castShadow = true;
    const l2 = new THREE.Mesh(legGeo, mat); l2.position.set(3, 3, 0); l2.castShadow = true;
    const top = new THREE.Mesh(new THREE.BoxGeometry(7.2, 1, 1), mat); top.position.set(0, 6, 0); top.castShadow = true;
    arch.add(l1, l2, top);
    arch.position.set(-22, 0, -20);
    arch.rotation.y = 0.6;
    this.group.add(arch);
  }

  /** Grama baixa varrida pelo vento (anima no update) + arbustos esparsos. */
  private buildFoliage(): void {
    const grassGeo = new THREE.ConeGeometry(0.16, 0.55, 4);
    const grassMat = new THREE.MeshStandardMaterial({ color: 0x5b7f8f, roughness: 0.9, flatShading: true });
    const count = 150;
    this.grass = new THREE.InstancedMesh(grassGeo, grassMat, count);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    for (let i = 0; i < count; i++) {
      const x = randRange(-this.half + 3, this.half - 3);
      const z = randRange(-this.half + 3, this.half - 3);
      const s = randRange(0.6, 1.3);
      q.setFromEuler(new THREE.Euler(0, randRange(0, 6), 0));
      m.compose(new THREE.Vector3(x, s * 0.28, z), q, new THREE.Vector3(s, s, s));
      this.grass.setMatrixAt(i, m);
      this.grassBase.push(m.clone());
    }
    this.grass.instanceMatrix.needsUpdate = true;
    this.group.add(this.grass);

    const bushGeo = new THREE.IcosahedronGeometry(0.55, 0);
    const bushMat = new THREE.MeshStandardMaterial({ color: 0x4a6f7a, roughness: 0.95, flatShading: true });
    const bush = new THREE.InstancedMesh(bushGeo, bushMat, 26);
    for (let i = 0; i < 26; i++) { // 10.9HZ — era round(26×dens): passava da capacidade do InstancedMesh (setMatrixAt fora do range é no-op) e só gastava tempo na construção
      let x = 0, z = 0;
      for (let t = 0; t < 6; t++) { x = randRange(-this.half + 4, this.half - 4); z = randRange(-this.half + 4, this.half - 4); if (Math.hypot(x, z) > 11) break; }
      const s = randRange(0.7, 1.5);
      q.setFromEuler(new THREE.Euler(0, randRange(0, 6), 0));
      m.compose(new THREE.Vector3(x, s * 0.45, z), q, new THREE.Vector3(s, s * 0.8, s));
      bush.setMatrixAt(i, m);
    }
    bush.instanceMatrix.needsUpdate = true;
    bush.castShadow = true;
    this.group.add(bush);
  }

  /** Fragmentos de pedra suspensos (bobbing no update) — reforça verticalidade/ilhas. */
  private buildFloatingFragments(): void {
    const geo = new THREE.DodecahedronGeometry(0.7, 0);
    const mat = new THREE.MeshStandardMaterial({ color: 0x8299b4, roughness: 1, flatShading: true });
    const inst = new THREE.InstancedMesh(geo, mat, 18);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    for (let i = 0; i < 18; i++) { // 10.9HZ — era round(18×dens): passava da capacidade do InstancedMesh (setMatrixAt fora do range é no-op) e só gastava tempo na construção
      let x = 0, z = 0;
      for (let t = 0; t < 6; t++) { x = randRange(-this.half + 6, this.half - 6); z = randRange(-this.half + 6, this.half - 6); if (Math.hypot(x, z) > 12) break; }
      const s = randRange(0.5, 1.3);
      q.setFromEuler(new THREE.Euler(randRange(0, 3), randRange(0, 3), randRange(0, 3)));
      m.compose(new THREE.Vector3(x, randRange(2.5, 5.5), z), q, new THREE.Vector3(s, s, s));
      inst.setMatrixAt(i, m);
    }
    inst.instanceMatrix.needsUpdate = true;
    inst.castShadow = true;
    this.group.add(inst);
  }

  /** Céu tempestuoso: nuvens estilizadas altas (drift) + relâmpago distante SÓ visual. */
  private buildSky(): void {
    const cloudMat = new THREE.MeshStandardMaterial({ color: 0x3a4f6e, roughness: 1, transparent: true, opacity: 0.7, flatShading: true });
    for (let i = 0; i < Math.round(12 * this.dens); i++) {
      const cloud = new THREE.Mesh(new THREE.IcosahedronGeometry(randRange(4, 8), 0), cloudMat);
      cloud.scale.set(randRange(1.4, 2.6), 0.5, randRange(1.2, 2));
      cloud.position.set(randRange(-this.half, this.half), randRange(24, 34), randRange(-this.half, this.half));
      this.cloudGroup.add(cloud);
    }
    this.group.add(this.cloudGroup);

    // névoa de altitude (planos translúcidos baixos, tom frio)
    const mistMat = new THREE.MeshBasicMaterial({ color: 0xaebfd6, transparent: true, opacity: 0.06, depthWrite: false, side: THREE.DoubleSide });
    for (let i = 0; i < Math.round(4 * this.dens); i++) {
      const mist = new THREE.Mesh(new THREE.PlaneGeometry(this.half * 2, this.half * 2), mistMat);
      mist.rotation.x = -Math.PI / 2;
      mist.position.y = 8 + i * 4;
      this.group.add(mist);
    }

    // relâmpago distante: um "flash" emissivo numa nuvem alta longínqua (sem dano — §10)
    this.lightningMat = new THREE.MeshBasicMaterial({ color: 0xdfe9ff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
    const flash = new THREE.Mesh(new THREE.IcosahedronGeometry(6, 0), this.lightningMat);
    flash.scale.set(2, 0.6, 1.5);
    flash.position.set(randRange(-this.half, this.half), 30, -this.half - 6);
    this.group.add(flash);
  }

  private buildBoundary(): void {
    const geo = new THREE.OctahedronGeometry(1.8, 0);
    const mat = new THREE.MeshStandardMaterial({ color: 0x5c6f88, roughness: 1, flatShading: true });
    const inst = new THREE.InstancedMesh(geo, mat, 64);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const r = this.half + 1.5;
    for (let i = 0; i < 64; i++) { // 10.9HZ — era round(64×dens): passava da capacidade do InstancedMesh (setMatrixAt fora do range é no-op) e só gastava tempo na construção
      const a = (i / 64) * Math.PI * 2;
      const s = randRange(1.2, 2.8);
      q.setFromEuler(new THREE.Euler(randRange(0, 3), randRange(0, 3), randRange(0, 3)));
      m.compose(new THREE.Vector3(Math.cos(a) * r + randRange(-1, 1), randRange(-0.4, 1.8), Math.sin(a) * r + randRange(-1, 1)), q, new THREE.Vector3(s, s * randRange(1, 1.8), s));
      inst.setMatrixAt(i, m);
    }
    inst.instanceMatrix.needsUpdate = true;
    inst.castShadow = true;
    this.group.add(inst);

    // debris de vento: pequenos quads que derivam (pooled via InstancedMesh, anima no update)
    const dGeo = new THREE.PlaneGeometry(0.18, 0.18);
    const dMat = new THREE.MeshBasicMaterial({ color: 0xcfe0f4, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide });
    this.debris = new THREE.InstancedMesh(dGeo, dMat, DEBRIS);
    const dm = new THREE.Matrix4();
    for (let i = 0; i < DEBRIS; i++) { // 10.9HY — era round(40×dens)=349 num buffer de 40: 309 iterações por quadro liam NaN e escreviam fora do range
      dm.setPosition(randRange(-this.half, this.half), randRange(0.5, 4), randRange(-this.half, this.half));
      this.debris.setMatrixAt(i, dm);
    }
    this.debris.instanceMatrix.needsUpdate = true;
    this.group.add(this.debris);
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  update(dt: number): void {
    if (!this.group.visible) return;
    this.pulse += dt;

    // cristais: pulso emissivo
    const e = 0.6 + Math.sin(this.pulse * 1.5) * 0.3;
    for (const m of this.crystalMats) m.emissiveIntensity = e;
    // arcos: flicker rápido (energia)
    const ao = 0.35 + Math.abs(Math.sin(this.pulse * 6)) * 0.4;
    for (const m of this.arcMats) m.opacity = ao;

    // nuvens: drift lento em X (vento visual)
    this.cloudGroup.position.x = Math.sin(this.pulse * 0.05) * 6;

    // vento na grama: leve sway (recompoe do baseline com uma inclinação senoidal)
    const sway = Math.sin(this.pulse * 2) * 0.14;
    const m = new THREE.Matrix4();
    const tilt = new THREE.Matrix4().makeRotationX(sway);
    for (let i = 0; i < this.grassBase.length; i++) {
      m.multiplyMatrices(this.grassBase[i], tilt);
      this.grass.setMatrixAt(i, m);
    }
    this.grass.instanceMatrix.needsUpdate = true;

    // debris: deriva horizontal contínua com wrap
    const dm = new THREE.Matrix4();
    const drift = (this.pulse * 3) % (this.half * 2);
    for (let i = 0; i < DEBRIS; i++) { // 10.9HY — era round(40×dens)=349 num buffer de 40: 309 iterações por quadro liam NaN e escreviam fora do range
      this.debris.getMatrixAt(i, dm);
      const px = ((dm.elements[12] + this.half + (i % 2 === 0 ? drift * 0.001 : 0)) % (this.half * 2)) - this.half;
      dm.elements[12] = px + Math.sin(this.pulse + i) * 0.02;
      dm.elements[14] += Math.cos(this.pulse * 0.5 + i) * 0.01;
      this.debris.setMatrixAt(i, dm);
    }
    this.debris.instanceMatrix.needsUpdate = true;

    // relâmpago distante: flash ocasional (só visual, sem dano)
    this.lightningTimer -= dt;
    if (this.lightningTimer <= 0) {
      this.lightningMat.opacity = 0.85;
      this.lightningTimer = randRange(3.5, 8);
    } else {
      this.lightningMat.opacity *= Math.exp(-6 * dt);
    }
  }
}
